import type {
  CatalogBlock,
  CatalogPreviewPage,
  CatalogPreviewPageItem,
  HeaderItem,
} from '@/types/catalog'
import type { AdaptedProduct } from '@/services/property'

const CM_TO_PX = 96 / 2.54
const PT_TO_PX = 96 / 72

export const ROTOR_PAGE_WIDTH_CM = 21
export const ROTOR_PAGE_HEIGHT_CM = 29.7
export const ROTOR_PAGE_PADDING_TOP_CM = 1.778
export const ROTOR_PAGE_PADDING_RIGHT_CM = 1.778
export const ROTOR_PAGE_PADDING_BOTTOM_CM = 0.973
export const ROTOR_PAGE_PADDING_LEFT_CM = 1.778
export const ROTOR_PAGE_AVAILABLE_HEIGHT_CM = 26.0
export const ROTOR_PRODUCT_ITEM_HEIGHT_CM = 6.4
export const ROTOR_HEADER_CATEGORY_HEIGHT_CM = 1.3
export const ROTOR_HEADER_LOT_HEIGHT_CM = 1.2
export const ROTOR_DESCRIPTION_PADDING_CM = 1.0
export const ROTOR_DESCRIPTION_MAX_HEIGHT_CM = 4.4
export const ROTOR_DESCRIPTION_LINE_HEIGHT_CM = 0.47
export const ROTOR_DESCRIPTION_CHARS_PER_LINE = 85
export const ROTOR_HEADER_CATEGORY_MIN_HEIGHT_CM = 3.175
export const ROTOR_PAGE_HEIGHT_TOLERANCE_CM = 0.1

export const rotorCmToPx = (value: number) => value * CM_TO_PX
export const rotorPtToPx = (value: number) => value * PT_TO_PX

export const ROTOR_PAGE_WIDTH_PX = rotorCmToPx(ROTOR_PAGE_WIDTH_CM)
export const ROTOR_PAGE_HEIGHT_PX = rotorCmToPx(ROTOR_PAGE_HEIGHT_CM)
export const ROTOR_PAGE_PADDING_TOP_PX = rotorCmToPx(ROTOR_PAGE_PADDING_TOP_CM)
export const ROTOR_PAGE_PADDING_RIGHT_PX = rotorCmToPx(
  ROTOR_PAGE_PADDING_RIGHT_CM,
)
export const ROTOR_PAGE_PADDING_BOTTOM_PX = rotorCmToPx(
  ROTOR_PAGE_PADDING_BOTTOM_CM,
)
export const ROTOR_PAGE_PADDING_LEFT_PX = rotorCmToPx(
  ROTOR_PAGE_PADDING_LEFT_CM,
)

type RotorPaginationEntry<T> = {
  item: T
  type: 'HEADER' | 'PRODUCT'
  lot?: string
  heightCm: number
}

const isHeaderItem = (
  item: HeaderItem | AdaptedProduct,
): item is HeaderItem => {
  return (
    typeof item === 'object' &&
    item !== null &&
    'type' in item &&
    item.type === 'HEADER' &&
    'category' in item &&
    typeof item.category === 'string'
  )
}

const getHeaderDescriptionText = (header: HeaderItem): string => {
  const descriptions = [
    header.description,
    header.descriptionEN,
    header.descriptionNL,
  ].filter((description): description is string => Boolean(description?.length))

  if (!descriptions.length) return ''

  return descriptions.reduce((longest, current) =>
    current.length > longest.length ? current : longest,
  )
}

export const estimateRotorDescriptionHeightCm = (
  description: string,
): number => {
  if (!description.length) return 0

  const totalLines = description
    .split('\n')
    .reduce(
      (sum, line) =>
        sum +
        Math.max(1, Math.ceil(line.length / ROTOR_DESCRIPTION_CHARS_PER_LINE)),
      0,
    )

  const textHeight = totalLines * ROTOR_DESCRIPTION_LINE_HEIGHT_CM

  return Math.min(
    textHeight + ROTOR_DESCRIPTION_PADDING_CM,
    ROTOR_DESCRIPTION_MAX_HEIGHT_CM,
  )
}

export const estimateRotorHeaderHeightCm = (header: HeaderItem): number => {
  let height = ROTOR_HEADER_CATEGORY_HEIGHT_CM

  if (header.lot) height += ROTOR_HEADER_LOT_HEIGHT_CM

  const descriptionText = getHeaderDescriptionText(header)
  if (descriptionText) {
    height += estimateRotorDescriptionHeightCm(descriptionText)
  }

  if (!header.lot) {
    height = Math.max(height, ROTOR_HEADER_CATEGORY_MIN_HEIGHT_CM)
  }

  return height
}

export const estimateRotorItemHeightCm = (
  item: HeaderItem | AdaptedProduct,
): number => {
  return isHeaderItem(item)
    ? estimateRotorHeaderHeightCm(item)
    : ROTOR_PRODUCT_ITEM_HEIGHT_CM
}

export const getRotorBindingSide = (
  pageIndex: number,
): CatalogPreviewPage['bindingSide'] => {
  return pageIndex % 2 === 0 ? 'left' : 'right'
}

export const getRotorBindingPaddingPx = (binding: unknown) => {
  if (typeof binding !== 'number' || Number.isNaN(binding) || binding <= 0) {
    return 0
  }

  // React-PDF interprets numeric spacing values as pt.
  return rotorPtToPx(binding)
}

export const paginateRotorEntries = <T>(
  entries: RotorPaginationEntry<T>[],
): T[][] => {
  const pages: T[][] = []
  let currentPage: T[] = []
  let currentHeight = 0
  const exceedsPageHeight = (nextHeight: number) =>
    nextHeight > ROTOR_PAGE_AVAILABLE_HEIGHT_CM + ROTOR_PAGE_HEIGHT_TOLERANCE_CM

  entries.forEach((entry) => {
    if (entry.type === 'HEADER') {
      const neededHeight = entry.heightCm + ROTOR_PRODUCT_ITEM_HEIGHT_CM
      const mustStartFreshPage = Boolean(entry.lot) && currentPage.length > 0

      if (
        mustStartFreshPage ||
        (currentPage.length > 0 &&
          exceedsPageHeight(currentHeight + neededHeight))
      ) {
        pages.push(currentPage)
        currentPage = []
        currentHeight = 0
      }
    } else if (
      currentPage.length > 0 &&
      exceedsPageHeight(currentHeight + entry.heightCm)
    ) {
      pages.push(currentPage)
      currentPage = []
      currentHeight = 0
    }

    currentPage.push(entry.item)
    currentHeight += entry.heightCm
  })

  if (currentPage.length > 0) {
    pages.push(currentPage)
  }

  return pages
}

const buildRotorPreviewPageEntries = (
  blocks: CatalogBlock[],
): RotorPaginationEntry<CatalogPreviewPageItem>[] =>
  blocks.map((block, blockIndex) => {
    const heightCm = estimateRotorItemHeightCm(block.data)

    return {
      item: {
        id: `${block.id}-${blockIndex}`,
        blockId: block.id,
        blockIndex,
        type: block.type,
        block,
        heightCm,
        heightPx: rotorCmToPx(heightCm),
      },
      type: block.type,
      lot: block.type === 'HEADER' ? block.data.lot : undefined,
      heightCm,
    }
  })

export const buildRotorPreviewPages = (
  blocks: CatalogBlock[],
  firstPageNumber = 1,
): CatalogPreviewPage[] => {
  const entries = buildRotorPreviewPageEntries(blocks)
  return paginateRotorEntries(entries).map((items, pageIndex) => ({
    pageIndex,
    displayPageNumber: firstPageNumber + pageIndex,
    bindingSide: getRotorBindingSide(pageIndex),
    items,
  }))
}

export const getRotorPdfPagesFromPreviewPages = (
  pages: CatalogPreviewPage[],
): Array<Array<HeaderItem | AdaptedProduct>> =>
  pages.map((page) => page.items.map((item) => item.block.data))

export const buildRotorPdfPages = (
  blocks: CatalogBlock[],
): Array<Array<HeaderItem | AdaptedProduct>> =>
  getRotorPdfPagesFromPreviewPages(buildRotorPreviewPages(blocks))
